import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import {
  PLANS_V2, PLAN_LIMITS, canAccessFeature, DEGRAU_ESCADA_ANTIGA, DEGRAU_ESCADA_NOVA,
  type PlanKey, type PremiumFeature,
} from "@/lib/plans";
import { planoDaAssinatura, STATUS_DE_ASSINATURA_COM_ACESSO } from "@/lib/vitrine/pricing";
import { temRecurso } from "@/lib/plans-v3";

/**
 * Duas escadas, e a conta pertence a UMA delas.
 *
 * "premium" é o terceiro degrau na escada antiga (R$ 29,90, 5 imóveis) e o
 * quarto na nova (R$ 129,90, 30 imóveis). Misturar as duas num único ranking
 * daria a quem assinou o premium antigo os portões do premium novo. Quem diz em
 * qual escada a conta está é o BANCO: `get_current_plan_limits` devolve o
 * `pricing_version` da linha que a RLS usou.
 *
 * As duas moram em `@/lib/plans` desde 10/09/2026: havia uma cópia aqui e o
 * portão de recurso precisava delas também. Duas cópias da mesma escada é
 * exatamente a forma como o preço deste produto já morou em cinco lugares.
 */

/**
 * O mapa abaixo é o ÚLTIMO resquício de identificador de preço escrito à mão, e
 * ele sobrevive por um motivo estreito: é o socorro de limites usado só quando
 * `get_current_plan_limits` não responde, e só para contas da escada v2. Quem
 * resolve o PLANO passou a ser `planoDaAssinatura`, pelo catálogo do banco.
 */
// AC-4 (Story 6.1): V2 price IDs → PLANS_V2 key para cálculo correto de limites quando planos_v2_enabled
const PRICE_TO_V2_KEY: Partial<Record<string, keyof typeof PLANS_V2>> = {
  "price_1TkvRqD85maAQgQctorlCVOJ": "starter",  // Starter Mensal
  "price_1TkvRsD85maAQgQcp0dk9pqw": "starter",  // Starter Anual
  "price_1TkvRtD85maAQgQcKJaeOyTq": "premium",  // Premium V2 Mensal → 10 imóveis
  "price_1TkvRuD85maAQgQcYGRbnIDX": "premium",  // Premium V2 Anual
  "price_1TkvRvD85maAQgQcRUjtnGsY": "plus",     // Plus V2 Mensal
  "price_1TkvRwD85maAQgQcBTzvauTN": "plus",     // Plus V2 Anual
  "price_1TkvRyD85maAQgQcV9uTdwfr": "business", // Business Mensal
  "price_1TkvRzD85maAQgQcg6kUJdjS": "business", // Business Anual
};

interface UsePlanReturn {
  plan: PlanKey;
  isLoading: boolean;
  isFree: boolean;
  isStarter: boolean;
  isPremium: boolean;
  isPlus: boolean;
  /** Plano do catálogo novo, quando a conta está nele. */
  isEssential: boolean;
  isProfessional: boolean;
  isEnterprise: boolean;
  limits: { imoveis: number; contratos: number };
  /** O Stripe tentou cobrar e ainda não conseguiu. O acesso continua; o aviso é devido. */
  cobrancaPendente: boolean;
  /**
   * Decidido em 02/10: nunca teve assinatura nenhuma, e os 3 meses grátis do
   * Free já terminaram. É o veredito do servidor (`resolve_plan_limit_key`),
   * não uma conta de dias feita aqui — `ProtectedRoute` trava a conta nisto.
   */
  periodoGratisExpirado: boolean;
  /** A RPC de limites ainda não respondeu. Enquanto true, não decida a trava. */
  carregandoLimites: boolean;
  /** Qual escada de preços vale para esta conta, segundo o banco. */
  pricingVersion: string | null;
  canAccess: (minPlan: PlanKey) => boolean;
  podeUsarRecurso: (feature: PremiumFeature) => boolean;
  /**
   * O recurso está incluído no plano, segundo a matriz `plan_features` do
   * catálogo. É o vocabulário do catálogo (`gifi_intelligence`, `gifi_hub`,
   * `api_integracao`), não o de `PremiumFeature` — os dois não se correspondem
   * um a um, e é o mesmo que o servidor consulta.
   */
  temRecursoDoCatalogo: (featureKey: string) => boolean;
}

export function usePlan(): UsePlanReturn {
  const { user } = useAuth();

  const { data: subInfo, isLoading } = useQuery({
    queryKey: ["subscription-plan", user?.id],
    queryFn: async (): Promise<{ plan: PlanKey; priceId: string | null }> => {
      if (!user?.id) return { plan: "free", priceId: null };
      // O catálogo vem junto porque é ELE que identifica a escada vigente: os
      // identificadores de preço são do ambiente e não podem morar no pacote.
      const [assinatura, catalogo] = await Promise.all([
        supabase
          .from("subscriptions")
          .select("status, price_id, product_id, subscription_type, plan_key")
          .eq("user_id", user.id)
          .in("status", [...STATUS_DE_ASSINATURA_COM_ACESSO])
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("plans")
          .select("plan_key, stripe_price_id_monthly, stripe_price_id_annual")
          .eq("active", true),
      ]);
      if (assinatura.error || !assinatura.data) return { plan: "free", priceId: null };
      // `planoDaAssinatura` é o mesmo resolvedor que a Vitrine e o servidor
      // usam. Antes daqui havia um mapa fixo de identificadores de preço,
      // escrito à mão em 2025, que não conhecia NENHUM preço da escada vigente:
      // quem assinasse o Essencial apareceria como "free" em toda a interface.
      // A terceira posição é a lista de situações, e ela é passada de propósito:
      // o padrão do resolvedor é o ESTRITO (sem `past_due`), que serve a quem
      // decide gasto. Aqui se decide ACESSO, e cobrança em curso não tira o
      // acesso de ninguém. Sem este argumento, a conta com cartão recusado
      // apareceria como "gratuita" na interface enquanto o banco lhe dava o
      // plano cheio — errado nos dois sentidos, de novo.
      const plano = planoDaAssinatura(
        assinatura.data, catalogo.data ?? [], STATUS_DE_ASSINATURA_COM_ACESSO,
      );
      return {
        plan: (plano as PlanKey) ?? "free",
        priceId: assinatura.data.price_id ?? null,
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const resolvedPlan: PlanKey = subInfo?.plan ?? "free";
  const rawPriceId: string | null = subInfo?.priceId ?? null;
  const isFree = resolvedPlan === "free";

  // FUND-0.2: os limites vêm da tabela plan_limits, a mesma fonte usada pelas policies
  // de INSERT. Antes eram calculados aqui a partir de PLAN_LIMITS/PLANS_V2, o que podia
  // divergir do que o banco realmente permite.
  const { data: dbLimits, isLoading: limitsLoading } = useQuery({
    queryKey: ["plan-limits", user?.id],
    queryFn: async (): Promise<{
      imoveis: number; contratos: number; versao: string | null; situacao: string | null;
      limitKey: string | null;
    } | null> => {
      const { data, error } = await supabase.rpc("get_current_plan_limits" as any);
      const row = Array.isArray(data) ? (data[0] as any) : null;
      if (error || !row) return null;
      // NULL no banco significa ilimitado; a UI representa isso como Infinity.
      return {
        imoveis: row.max_properties ?? Infinity,
        contratos: row.max_contracts ?? Infinity,
        versao: (row.pricing_version as string | null) ?? null,
        // A situação vem da MESMA chamada que decidiu o limite. Consultar
        // `subscriptions` por fora para saber isto responderia por uma linha
        // possivelmente diferente da que o banco usou.
        situacao: (row.subscription_status as string | null) ?? null,
        // A chave de limite é o próprio veredito do servidor sobre esta conta —
        // inclusive se o período gratuito acabou sem nunca ter havido assinatura
        // (ver `resolve_plan_limit_key`, 02/10). Ler o veredito aqui evita
        // reimplementar a mesma conta de dias em JavaScript, que já existe em
        // `plans-v3.ts` só para o AVISO — nunca para decidir acesso.
        limitKey: (row.limit_key as string | null) ?? null,
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Fallback local — usado só se a RPC falhar (offline, erro de rede).
  // AC-4 (Story 6.1): com planos_v2_enabled ativo, usa PLANS_V2 (AC-8: v1 não afetado).
  const v2Enabled = useFeatureFlag("planos_v2_enabled");
  const v2PlanKey = v2Enabled && rawPriceId ? PRICE_TO_V2_KEY[rawPriceId] : null;
  const fallbackLimits = v2PlanKey ? PLANS_V2[v2PlanKey].limits : PLAN_LIMITS[resolvedPlan];

  // O A/B ab_free_2imoveis foi removido daqui: as duas variantes resultavam em 2 imóveis
  // desde que PLAN_LIMITS.free passou a ser 2, então o código não tinha mais efeito.
  const effectiveLimits = dbLimits
    ? { imoveis: dbLimits.imoveis, contratos: dbLimits.contratos }
    : fallbackLimits;

  // Sem resposta do banco, a escada é a que contém o nome do plano — e, no
  // empate ("premium" está nas duas), vale a antiga, que é onde estão as contas
  // de hoje.
  const escada = dbLimits?.versao === "v3" ? DEGRAU_ESCADA_NOVA
    : dbLimits?.versao ? DEGRAU_ESCADA_ANTIGA
    : (resolvedPlan in DEGRAU_ESCADA_ANTIGA ? DEGRAU_ESCADA_ANTIGA : DEGRAU_ESCADA_NOVA);

  return {
    plan: resolvedPlan,
    isLoading: isLoading && !!user,
    isFree,
    isStarter: resolvedPlan === "starter",
    isPremium: resolvedPlan === "premium",
    isPlus: resolvedPlan === "plus",
    isEssential: resolvedPlan === "essential",
    isProfessional: resolvedPlan === "professional",
    isEnterprise: resolvedPlan === "enterprise",
    limits: effectiveLimits,
    // A cobrança falhou e o Stripe ainda está tentando. O acesso continua —
    // ver `STATUS_DE_ASSINATURA_COM_ACESSO` —, e a tela precisa dizer isso,
    // senão o produto esconde uma cobrança que não passou.
    cobrancaPendente: dbLimits?.situacao === "past_due",
    // Decidido em 02/10: nunca teve assinatura nenhuma, e os 3 meses grátis
    // acabaram. `isLoading` cobre a query de assinatura; esta cobre a RPC de
    // limites — enquanto ela não respondeu, "ainda não sei" nunca é "sim".
    periodoGratisExpirado: dbLimits?.limitKey === "plan:free_expirado",
    carregandoLimites: limitsLoading,
    pricingVersion: dbLimits?.versao ?? null,
    canAccess: (minPlan: PlanKey) => {
      const meu = escada[resolvedPlan];
      const pedido = escada[minPlan];
      // Plano fora desta escada não passa: falhar fechado é o certo num portão.
      if (meu === undefined || pedido === undefined) return false;
      return meu >= pedido;
    },
    // O portão de recurso precisa saber QUAL escada vale para esta conta, e só
    // aqui isso é sabido: quem chama `canAccessFeature` solto passa o nome do
    // plano e "premium" é ambíguo entre as duas.
    podeUsarRecurso: (feature: PremiumFeature) =>
      canAccessFeature(resolvedPlan, feature, dbLimits?.versao ?? null),
    temRecursoDoCatalogo: (featureKey: string) => {
      // O topo da escada antiga não está no catálogo v3 e tinha acesso a tudo
      // que era "Plus". Continua tendo: tirar direito de quem já paga por causa
      // de uma reorganização de catálogo é a pior forma de corrigir. É a mesma
      // ponte que `_shared/plan-catalog.ts` faz no servidor.
      if (resolvedPlan === "plus" || resolvedPlan === "business") return true;
      return temRecurso(resolvedPlan, featureKey);
    },
  };
}
